import { Check, Phone, ArrowRight } from "lucide-react";

const Pricing = () => {
  const plans = [
    {
      name: "Single Platform",
      price: "25,000",
      description: "Launch on 1 marketplace",
      popular: false,
      features: [
        "1 Platform Onboarding",
        "Complete Documentation",
        "Catalog Creation (up to 50 SKUs)",
        "Brand Verification Support"
      ]
    },
    {
      name: "Growth Bundle",
      price: "45,000",
      description: "Launch on 3 marketplaces",
      popular: true,
      features: [
        "3 Platform Onboarding",
        "Complete Documentation",
        "Catalog Creation (up to 150 SKUs)",
        "Brand Verification Support",
        "Dedicated Growth Manager",
        "7-Day Post-Launch Support",
        "Listing Optimization"
      ]
    },
    {
      name: "Enterprise",
      price: "70,000",
      description: "Full marketplace domination",
      popular: false,
      features: [
        "5 Platform Onboarding",
        "Complete Documentation",
        "Unlimited SKU Catalog",
        "Brand Verification Support",
        "Dedicated Growth Manager",
        "30-Day Post-Launch Support",
        "Listing Optimization",
        "Ad Campaign Setup",
        "Monthly Growth Reports"
      ]
    }
  ];

  return (
    <section id="pricing" className="py-16 md:py-24 px-4 bg-gradient-to-b from-white via-rose-50/60 to-violet-50/50 relative overflow-hidden" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold mb-4">
            <span>TRANSPARENT PRICING</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-black mb-4">
            Simple, Honest <span className="text-red-600">Pricing</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            No hidden fees. Choose the plan that fits your brand's needs.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative rounded-xl p-6 lg:p-8 border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-2 ${
                plan.popular
                  ? "border-red-600 bg-white shadow-lg shadow-red-600/10 scale-[1.02]"
                  : "border-gray-200 bg-white hover:border-blue-500"
              }`}
              data-testid={`pricing-card-${idx}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-red-600 text-white text-xs font-bold rounded-lg">
                  MOST POPULAR
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-black text-black mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.description}</p>
              </div>

              <div className="mb-6">
                <span className="text-sm text-gray-500">Starting at</span>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-black text-black">&#8377;{plan.price}</span>
                  <span className="text-gray-500 text-sm">/one-time</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, fIdx) => (
                  <li key={fIdx} className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <a
                href={`https://wa.me/919464645491?text=${encodeURIComponent(`Hi BrandBorn! I want to onboard my brand. Interested in ${plan.name} plan (₹${plan.price}). Please share details.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl font-black text-sm tracking-wider uppercase transition-all duration-500 hover:-translate-y-1 ${
                  plan.popular
                    ? "bg-red-600 text-white hover:bg-red-700 shadow-[0_4px_15px_rgba(220,38,38,0.3)] hover:shadow-[0_10px_30px_rgba(220,38,38,0.4)]"
                    : "bg-black text-white hover:bg-gray-900 shadow-[0_4px_15px_rgba(0,0,0,0.2)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.3)]"
                }`}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/></svg>
                Book Now on WhatsApp
                <ArrowRight size={16} />
              </a>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-3">Need a custom plan for your brand?</p>
          <a
            href="tel:+919464645491"
            className="inline-flex items-center gap-2 text-red-600 font-bold hover:text-red-700 transition"
          >
            <Phone size={18} />
            Call +91-9464645491 for Custom Quote
          </a>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
